import Kbd from '@/components/Kbd.jsx';
import { FiChevronRight } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2.jsx';
import { setContextMenuTarget } from '@/redux/actions/uiActions/contextMenuActions';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors';
import { autoUpdate, flip, FloatingFocusManager, FloatingOverlay, FloatingPortal, offset, shift, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const findParentDistance = (element, selector) => {
  let distance = 0;
  let current = element;
  while (current) {
    if (current.matches(selector)) {
      return distance;
    }
    current = current.parentElement;
    distance++;
  }
  return -1;
};

const getEventElementTarget = (target) => {
  if (!target) {
    return null;
  }

  if (target.nodeType === 1) {
    return target;
  }

  return target.parentElement || null;
};

const ContextMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [contextMenuItems, setContextMenuItems] = useState([]);
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [submenuState, setSubmenuState] = useState(null);
  const allowMouseUpCloseRef = useRef(false);
  const pathname = useSelector(selectors.pathname);
  const contextMenuData = useSelector(memoizeSelector('contextMenu', (state) => selectors.contextMenu(state)));

  const defaultMenu = [];
  const filteredMenu = useMemo(() => [...defaultMenu, ...contextMenuData].filter((item) => pathname.includes(item.route)), [pathname, contextMenuData]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        setSubmenuState(null);
        setContextMenuTarget(null);
      }
    },
    middleware: [
      offset({ mainAxis: 5, alignmentAxis: 4 }),
      flip({
        fallbackPlacements: ['left-start']
      }),
      shift({ padding: 10 })
    ],
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  });
  const role = useRole(context, { role: 'menu' });
  const dismiss = useDismiss(context, {
    outsidePress: (event) => !event.target?.closest('.comp-context-menu')
  });
  const { getFloatingProps } = useInteractions([role, dismiss]);

  const handleItemClick = (e, item) => {
    if (item?.children?.length) {
      return;
    }

    setIsOpen(false);
    setSubmenuState(null);
    item?.onClick(rightClickEvent, item);
  };

  const handleItemMouseEnter = (event, item) => {
    if (!item?.children?.length) {
      setSubmenuState(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const submenuWidth = 220;
    const left = rect.right + submenuWidth > window.innerWidth ? rect.left - submenuWidth + 6 : rect.right - 6;

    setSubmenuState({
      itemKey: item?.label,
      options: item?.children,
      x: left,
      y: Math.max(8, rect.top - 4)
    });
  };

  useEffect(() => {
    let timeout;

    function onContextMenu(e) {
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      const targetElement = getEventElementTarget(e.target);

      if (!targetElement) {
        return;
      }

      const blacklistedElements = ['.comp-modal-v2'];
      if (blacklistedElements.some((element) => targetElement.closest(element))) {
        return;
      }

      const matchedMenu = [...filteredMenu]
        .map((entry) => ({
          ...entry,
          options: entry?.options
            ?.filter((option) => option?.selectors.filter((n) => n === '' || targetElement.closest(n)).length)
            .map((option) => {
              const hasBlank = option?.selectors.includes('');
              const minDistance = Math.min(...option?.selectors.map((selector) => findParentDistance(targetElement, selector)).filter((n) => n >= 0));
              return {
                ...option,
                label: typeof option?.label === 'function' ? option?.label(e) : option?.label,
                distance: hasBlank ? 100 : minDistance
              };
            })
        }))
        ?.map((entry) => ({
          ...entry,
          distance: Math.max(...entry?.options?.map((n) => n?.distance))
        }))
        ?.filter((n) => n?.options?.length)
        ?.sort(sortByProp('distance', 'asc'));

      if (matchedMenu.length < 1) {
        return;
      }

      e.preventDefault();

      refs.setPositionReference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: e.clientX,
            y: e.clientY,
            top: e.clientY,
            right: e.clientX,
            bottom: e.clientY,
            left: e.clientX
          };
        }
      });

      setRightClickEvent(e);
      setContextMenuItems(matchedMenu);
      setSubmenuState(null);
      setIsOpen(true);
      clearTimeout(timeout);

      // Track the context menu target for highlighting
      const actionRow = targetElement.closest('.app-actions-row[data-item-type="action"]');
      const argumentRow = targetElement.closest('.argument-row');
      if (actionRow) {
        const actionId = actionRow.getAttribute('data-item-id');
        const selectedRows = actionRow.getAttribute('data-selected') === 'true' ? [...document.querySelectorAll('.app-actions-row[data-item-type="action"][data-selected="true"]')] : [actionRow];
        const actionIds = selectedRows.map((row) => row.getAttribute('data-item-id')).filter(Boolean);
        setContextMenuTarget({ type: 'appAction', id: actionId, ids: actionIds.length > 0 ? actionIds : [actionId].filter(Boolean) });
      } else if (argumentRow) {
        const argumentId = argumentRow.getAttribute('data-argument-id');
        setContextMenuTarget({ type: 'argument', id: argumentId });
      } else {
        setContextMenuTarget(null);
      }

      allowMouseUpCloseRef.current = false;
      timeout = window.setTimeout(() => {
        allowMouseUpCloseRef.current = true;
      }, 300);
    }

    function onMouseUp(e) {
      const isInsideContextMenu = e.target?.closest('.comp-context-menu');

      if (isInsideContextMenu) {
        return;
      }

      if (allowMouseUpCloseRef.current) {
        setIsOpen(false);
      }
    }

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('mouseup', onMouseUp);
      clearTimeout(timeout);
    };
  }, [refs, filteredMenu]);

  const getItemDisabled = (disabled) => {
    if (typeof disabled === 'function') {
      return disabled(rightClickEvent);
    }
    return disabled;
  };

  const visibleContextMenuItems = useMemo(() => {
    return contextMenuItems
      .map(({ selector, options }) => {
        const visibleOptions = (Array.isArray(options) ? options : []).filter((item) => !getItemDisabled(item.disabled ?? false));
        return visibleOptions.length > 0 ? { selector, options: visibleOptions } : null;
      })
      .filter(Boolean);
  }, [contextMenuItems, rightClickEvent]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e) => {
      const key = e.key;
      if (!key || ['Shift', 'Control', 'Alt', 'Meta'].includes(key)) {
        return;
      }

      const enabledOptions = visibleContextMenuItems.flatMap(({ options }) => (Array.isArray(options) ? options : []).flatMap((option) => [option, ...(Array.isArray(option?.children) ? option.children : [])]).filter((n) => !getItemDisabled(n.disabled ?? false) && !n?.children?.length));
      const matchingItem = enabledOptions.find((option) => option?.shortcut?.key?.toLowerCase() === key?.toLowerCase());

      if (matchingItem) {
        e.preventDefault();
        e.stopPropagation();
        handleItemClick(e, matchingItem);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, rightClickEvent, visibleContextMenuItems]);

  if (!isOpen) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll style={{ zIndex: 10000000 }}>
        <FloatingFocusManager context={context} initialFocus={refs.floating} returnFocus={false}>
          <div className="comp-context-menu" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            {visibleContextMenuItems.map(({ selector, options }, i) => (
              <Fragment key={`${selector}-${i}`}>
                {options.map((item) => (
                  <div key={item.label} role="menuitem" aria-disabled={getItemDisabled(item.disabled)} aria-haspopup={item?.children?.length ? 'menu' : undefined} tabIndex="-1" className={`item ${item?.children?.length ? 'with-children' : ''} ${submenuState?.itemKey === item?.label ? 'submenu-open' : ''}`} onClick={(e) => handleItemClick(e, item)} onMouseEnter={(e) => handleItemMouseEnter(e, item)}>
                    <div className="item-inner-wrapper">
                      <IconV2 size={12} className={'icon'} icon={item.icon} variant={'light-medium'} />
                      <span className="label">{item.label}</span>
                      {item?.shortcut && <Kbd>{item.shortcut.label}</Kbd>}
                      {item?.children?.length > 0 && <IconV2 size={12} className={'submenu-indicator'} icon={FiChevronRight} variant={'light-medium'} />}
                    </div>
                  </div>
                ))}
                <div role="separator" aria-orientation="horizontal" className="separator" />
              </Fragment>
            ))}
          </div>
          {submenuState?.options?.length > 0 && (
            <div className="comp-context-menu submenu" style={{ top: submenuState.y, left: submenuState.x }}>
              {submenuState.options
                .filter((n) => !getItemDisabled(n.disabled ?? false))
                .map((item) => (
                  <div key={item.label} role="menuitem" aria-disabled={getItemDisabled(item.disabled)} tabIndex="-1" className="item" onClick={(e) => handleItemClick(e, item)}>
                    <div className="item-inner-wrapper">
                      <IconV2 size={12} className={'icon'} icon={item.icon} variant={'light-medium'} />
                      <span className="label">{item.label}</span>
                      {item?.shortcut && <Kbd>{item.shortcut.label}</Kbd>}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
};

export default ContextMenu;
